// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {isEqual} from 'lodash';
import React, {useCallback} from 'react';
import {twMerge} from 'tailwind-merge';

import {useIsInLoadedReportContext} from '../../pages/ReportPage/LoadedReportContext';
import {getNwIdSearchString} from '../../state/workspaces/url';
import {useStatsigGateWorkspaceConsolidation} from '../../util/rampFeatureFlags';
import {runPath} from '../../util/runhelpers';
import * as Run from '../../util/runs';
import {Key} from '../../util/runTypes';
import * as RunTypes from '../../util/runTypes';
import {WBTableRowWithRunSetInfo} from '../../util/tablecols';
import {runGroup as runGroupURL} from '../../util/urls';
import {TruncateTextTooltip} from '../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {EditableLabel} from '../elements/EditableLabel';
import {RunRenameScope} from './types';

type LinkPathOpts = {
  disableRunLink: boolean;
  entityName: string;
  isGroup: boolean;
  isRunGroup: boolean;
  isSingleMode: boolean;
  projectName: string;
  rowName: string;
  isWorkspaceConsolidationEnabled?: boolean;
};

/**
 * Generates the URL for a run or run group in the run selector.
 * Returns undefined if the row should not be a link.
 * @param opts {@link LinkPathOpts}
 * @returns URL for a run or run group, or undefined if no link should be generated.
 */
export function linkPath({
  disableRunLink,
  entityName,
  isGroup,
  isRunGroup,
  isSingleMode,
  projectName,
  rowName,
  isWorkspaceConsolidationEnabled,
}: LinkPathOpts): string | undefined {
  if ((isGroup && !isRunGroup) || disableRunLink || isSingleMode) {
    return undefined;
  }

  let baseUrl;
  if (isRunGroup) {
    baseUrl = runGroupURL({
      entityName,
      projectName,
      name: rowName,
      tab: 'workspace',
    });
  } else {
    baseUrl = runPath({
      entityName,
      projectName,
      runName: rowName,
    });
  }
  return isWorkspaceConsolidationEnabled
    ? baseUrl + getNwIdSearchString()
    : baseUrl;
}

type RowHeaderLabelProps = {
  /**
   * Custom name to show for this run in place of the project-level
   * run.displayName. Custom run names are scoped to `viewSpec.section`
   * (i.e. multi-run workspace or panel grid in a report)
   */
  customRunName?: string;
  /** If true, no link to the row/run will be generated for the label */
  disableRunLink: boolean;
  /**
   * specifies the scope for which user wants to rename a run
   * (e.g. for entire project or just a specific panel grid).
   * if undefined, user is not editing the run name.
   */
  editingNameScope: RunRenameScope | undefined;
  entityName: string;
  /**
   * The property this row has been grouped by, if any. This can be
   * undefined if the row is not grouped, which does not match the typedef.
   */
  groupedBy: Key;
  /** True if this row is the "container row" for a group of runs */
  isGroup: boolean;
  /**
   * True if this group row is a group and the group-by parameter
   * is the literal run group that the run belongs to. If this is true,
   * isGroup will always be true.
   */
  isRunGroup: boolean;
  /**
   * True when we want clicks to a run to _select_ that run instead of
   * navigating somewhere else. This is used in run selector tables
   * inside of the /group/ views.
   */
  isSingleMode: boolean;
  onSaveName: (
    row: WBTableRowWithRunSetInfo,
    name: string,
    scope: RunRenameScope
  ) => void;
  onSelectRunName: (name: string) => void;
  projectName: string;
  row: WBTableRowWithRunSetInfo;
  truncationType: TruncationType;
  /**
   * Custom name to show when grouped by sweep
   */
  customSweepName: string | null | undefined;
};

/**
 * A component that can optionally allow the row header label to be edited. The
 * interface for this component is needlessly complex, but factoring it is out-
 * of-scope at the time of writing.
 * @param props {@link RowHeaderLabelProps}
 */
export function RowHeaderLabel({
  customRunName,
  customSweepName,
  disableRunLink,
  editingNameScope,
  entityName,
  groupedBy,
  isGroup,
  isRunGroup,
  isSingleMode,
  onSaveName,
  onSelectRunName,
  projectName,
  row,
  truncationType,
}: RowHeaderLabelProps) {
  const isReport = useIsInLoadedReportContext();

  const runGroupText = String(groupedBy ? Run.getValue(row, groupedBy) : '');
  let groupedText = isEqual(groupedBy, RunTypes.GROUP_BY_ALL_KEY)
    ? Run.keyDisplayName(groupedBy)
    : `${groupedBy ? Run.keyDisplayName(groupedBy) + ': ' : ''}${
        groupedBy ? runGroupText : ''
      }`;
  if (
    customSweepName &&
    (groupedBy?.name === 'sweep' || groupedBy?.name === 'sweepName')
  ) {
    groupedText = Run.keyDisplayName(groupedBy) + ': ' + customSweepName;
  }

  let text = row.displayName;
  if (isGroup) {
    text = groupedText;
  } else if (customRunName) {
    text = customRunName;
  }

  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();
  const link = linkPath({
    disableRunLink,
    entityName,
    isGroup,
    isRunGroup,
    isSingleMode,
    projectName,
    rowName: row.name,
    isWorkspaceConsolidationEnabled,
  });

  const handleTooltipClick = useCallback(() => {
    if (!link) {
      onSelectRunName(row.name);
    }
  }, [link, onSelectRunName, row.name]);

  if (editingNameScope) {
    let text = '';
    if (isRunGroup) {
      text = runGroupText;
    } else if (editingNameScope === 'project') {
      text = row.displayName;
    } else if (editingNameScope === 'view-section') {
      text = customRunName ?? '';
    }

    return (
      <EditableLabel
        editing
        extraMarginRight="18px"
        onBlur={(newName: string) => {
          onSaveName(row, newName, editingNameScope);
        }}
        placeholder={row.displayName}
        serverText={text}
      />
    );
  }

  return (
    <Tailwind>
      <div className="flex items-center gap-3">
        <TruncateTextTooltip
          className={twMerge(
            'text-left',
            !!customRunName && 'font-semibold',
            !!customRunName && 'w-[calc(100%_-_27px)] max-w-fit' // ensures info tooltip stays visible
          )}
          fullText={text}
          linkPath={link}
          noLink={!link}
          onClick={handleTooltipClick}
          truncationType={truncationType}
        />
        {customRunName != null && (
          <Button
            icon="info"
            variant="ghost"
            size="small"
            aria-label="More info on run name"
            tooltip={
              <>
                <span className="font-semibold">{customRunName}</span> is a
                custom display name for{' '}
                <span className="font-semibold">{row.displayName}</span> in this{' '}
                {isReport ? 'panel grid' : 'workspace'}.
              </>
            }
            tooltipProps={{className: 'max-w-[20rem]'}}
          />
        )}
      </div>
    </Tailwind>
  );
}
